import { isPlatformBrowser } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";

/**
 * Сервис для определения платформы, на которой выполняется приложение.
 *
 * Используется для проверки окружения выполнения кода - браузер (client-side)
 * или сервер (server-side при SSR/Angular Universal).
 *
 * @usageNotes
 *
 * Этот сервис критически важен для:
 * - Предотвращения ошибок при SSR (когда window/document недоступны)
 * - Условного выполнения browser-only кода (localStorage, DOM API и т.д.)
 * - Оптимизации рендеринга в зависимости от окружения
 *
 * @example
 * ```typescript
 * constructor(private platformService: PlatformService) {
 *     if (this.platformService.isPlatformBrowser) {
 *         // Безопасно использовать window, document, localStorage
 *         const data = localStorage.getItem('key');
 *         window.addEventListener('resize', this.onResize);
 *     }
 * }
 * ```
 *
 * @Injectable
 */
@Injectable({ providedIn: "root" })
export class PlatformService {
    readonly #platformId: object = inject(PLATFORM_ID);

    /**
     * Флаг, указывающий выполняется ли код в браузере.
     * `true` - код выполняется в браузере (client-side)
     * `false` - код выполняется на сервере (server-side при SSR)
     */
    public readonly isPlatformBrowser: boolean;

    constructor() {
        this.isPlatformBrowser = isPlatformBrowser(this.#platformId);
    }
}
